// localizedOffer.js — Centralized helpers for displaying opportunity content
// in the active UI language.
//
// Data model on the backend now stores optional `title_translations` and
// `description_translations` maps keyed by ISO 639-1 short codes
// (en/de/fr/es), plus a `source_language` and a `translation_status` map.
//
// These helpers NEVER do live translation. They simply pick the right
// pre-stored string with a controlled fallback chain so opportunity cards
// stop appearing half in one language and half in another.

const SUPPORTED = ["en", "de", "fr", "es"];

function normalizeLocale(locale) {
  if (!locale) return "en";
  const base = String(locale).split("-")[0].toLowerCase();
  return SUPPORTED.includes(base) ? base : "en";
}

function pickByStatus(translations, status, locale) {
  if (!translations) return null;
  const value = translations[locale];
  if (typeof value !== "string" || !value.trim()) return null;
  const s = status?.[locale];
  // Accept "source", "translated". Reject "needs_review" / "fallback" /
  // any unknown status — we'd rather fall back than show a flagged string.
  if (s && !(s === "source" || s === "translated")) return null;
  return value;
}

function buildLocalized(offer, locale, sourceField, translationsField) {
  const norm = normalizeLocale(locale);
  const tr = offer?.[translationsField];
  const status = offer?.translation_status;

  // 1. Translation for active locale.
  const exact = pickByStatus(tr, status, norm);
  if (exact) return exact;

  // 2. Source-language slot if active locale equals source language.
  const src = offer?.source_language;
  if (src && norm === src) {
    const fromMap = pickByStatus(tr, status, src);
    if (fromMap) return fromMap;
  }

  // 3. English fallback (most readable second choice for European users).
  if (norm !== "en") {
    const en = pickByStatus(tr, status, "en");
    if (en) return en;
  }

  // 4. Final fallback: original source field (never null/undefined).
  return offer?.[sourceField] || "";
}

export function getLocalizedOfferTitle(offer, locale) {
  return buildLocalized(offer, locale, "title", "title_translations");
}

export function getLocalizedOfferDescription(offer, locale) {
  return buildLocalized(offer, locale, "description", "description_translations");
}

/**
 * Returns the source language only if it is reliable AND differs from the
 * active locale AND no translation exists for the active locale. UI can use
 * this to show a small, honest "source language" hint. Returns null when no
 * hint should be shown.
 */
export function getOfferSourceLanguageHint(offer, locale) {
  const norm = normalizeLocale(locale);
  const src = offer?.source_language;
  if (!src || src === "unknown" || src === norm) return null;
  const tr = offer?.title_translations;
  const status = offer?.translation_status;
  const has = pickByStatus(tr, status, norm);
  return has ? null : src;
}
